import React, { useState, useEffect, useMemo, useCallback } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { Link, useLocation } from "react-router-dom";
import { getSidebarData } from "../../core/json/siderbar_data";
import HorizontalSidebar from "./horizontalSidebar";
import { all_routes } from "../../Router/all_routes";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { ChevronsLeft } from "feather-icons-react/build/IconComponents";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { publicRoutes, posRoutes } from "../../Router/router.link";
import { canSeeRoute } from "../../utils/perm";

const Sidebar = () => {
  const { i18n } = useTranslation();
  const route = all_routes;
  const location = useLocation();

  const [subOpen, setSubopen] = useState("");
  const [subsidebar, setSubsidebar] = useState("");
  const [sidebarData, setSidebarData] = useState(getSidebarData());
  const [toggle, SetToggle] = useState(false);

  // Redux selectors
  const expandMenus = useSelector((state) => state.themeSetting.expandMenus);
  const dataLayout = useSelector((state) => state.themeSetting.dataLayout);
  const user = useSelector((state) => state?.auth?.user);

  // ✅ memoize these so they don't "change every render"
  const userPerms = useMemo(() => user?.permissions ?? [], [user?.permissions]);

  const isAdmin = useMemo(() => {
    const roleIsAdmin = user?.role === "admin";
    const wildcard = userPerms.includes("*");
    return roleIsAdmin || wildcard;
  }, [user?.role, userPerms]);

  // helpers
  const routeByPath = useCallback(
    (path) => [...publicRoutes, ...posRoutes].find((r) => r.path === path),
    []
  );

  const linkAllowed = useCallback(
    (path) => {
      const r = routeByPath(path);
      if (!r) return true; // if path doesn't map to a route, don't hide it
      return canSeeRoute(userPerms, r, { isAdmin });
    },
    [routeByPath, userPerms, isAdmin]
  );

  // build filtered menu AFTER linkAllowed is available
  const filteredSidebarData = useMemo(() => {
    return (sidebarData || [])
      .map((main) => {
        const filteredSubmenuItems = (main.submenuItems || [])
          .map((title) => {
            const filteredChildrenLevel1 = (title.submenuItems || [])
              .map((item) => {
                const filteredChildrenLevel2 = (item.submenuItems || []).filter(
                  (x) => !x.link || linkAllowed(x.link)
                );

                const itemAllowed =
                  (item.link && linkAllowed(item.link)) ||
                  filteredChildrenLevel2.length > 0;

                if (!itemAllowed) return null;
                return { ...item, submenuItems: filteredChildrenLevel2 };
              })
              .filter(Boolean);

            const titleAllowed =
              (title.link && linkAllowed(title.link)) ||
              filteredChildrenLevel1.length > 0;

            if (!titleAllowed) return null;
            return { ...title, submenuItems: filteredChildrenLevel1 };
          })
          .filter(Boolean);

        if (filteredSubmenuItems.length === 0) return null;
        return { ...main, submenuItems: filteredSubmenuItems };
      })
      .filter(Boolean);
  }, [sidebarData, linkAllowed]);

  // react to language change
  useEffect(() => {
    setSidebarData(getSidebarData());
  }, [i18n.language]);

  useEffect(() => {
    const handleLanguageChange = () => setSidebarData(getSidebarData());
    window.addEventListener("languageChanged", handleLanguageChange);
    return () =>
      window.removeEventListener("languageChanged", handleLanguageChange);
  }, []);

  // ❌ This was causing re-renders every 500ms; remove unless truly needed.
  // useEffect(() => {
  //   const interval = setInterval(() => setSidebarData(getSidebarData()), 500);
  //   return () => clearInterval(interval);
  // }, []);

  const toggleSidebar = (title) => setSubopen((s) => (s === title ? "" : title));
  const toggleSubsidebar = (subitem) =>
    setSubsidebar((s) => (s === subitem ? "" : subitem));

  const handlesidebar = () => {
    document.body.classList.toggle("mini-sidebar");
    SetToggle((current) => !current);
  };

  const expandMenu = () => document.body.classList.remove("expand-menu");
  const expandMenuOpen = () => document.body.classList.add("expand-menu");

  return (
    <div>
      <div
        className={`sidebar ${toggle ? "" : "active"} ${
          expandMenus || dataLayout === "layout-hovered" ? "expand-menu" : ""
        }`}
        id="sidebar"
        onMouseLeave={expandMenu}
        onMouseOver={expandMenuOpen}
      >
        <>
          {/* Logo */}
          <div className="sidebar-logo">
            <Link to={route.newdashboard} className="logo logo-normal">
              <ImageWithBasePath src="assets/img/logo.svg" alt="Img" />
            </Link>
            <Link to={route.newdashboard} className="logo logo-white">
              <ImageWithBasePath src="assets/img/logo-white.svg" alt="Img" />
            </Link>
            <Link to={route.newdashboard} className="logo-small">
              <ImageWithBasePath src="assets/img/logo.jpg" alt="Img" />
            </Link>
            <Link id="toggle_btn" to="#" onClick={handlesidebar}>
              <ChevronsLeft className="feather-16" />
            </Link>
          </div>

          {/* Profile shortcuts */}
          <div className="modern-profile p-3 pb-0">
            <div className="text-center rounded bg-light p-3 mb-4 border">
              <div className="avatar avatar-lg online mb-3">
                <ImageWithBasePath
                  src="assets/img/customer/customer15.jpg"
                  alt="Img"
                  className="img-fluid rounded-circle"
                />
              </div>
              <h6 className="fs-14 fw-bold mb-1">Adrian Herman</h6>
              <p className="fs-12 mb-0">System Admin</p>
            </div>
            <div className="sidebar-nav mb-3">
              <ul
                className="nav nav-tabs nav-tabs-solid nav-tabs-rounded nav-justified bg-transparent"
                role="tablist"
              >
                <li className="nav-item">
                  <Link className="nav-link active border-0" to="#">
                    Menu
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link border-0" to={route.chat}>
                    Chats
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link border-0" to={route.email}>
                    Inbox
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="sidebar-header p-3 pb-0 pt-2">
            <div className="text-center rounded bg-light p-2 mb-4 sidebar-profile d-flex align-items-center">
              <div className="avatar avatar-md onlin">
                <ImageWithBasePath
                  src="assets/img/customer/customer15.jpg"
                  alt="Img"
                  className="img-fluid rounded-circle"
                />
              </div>
              <div className="text-start sidebar-profile-info ms-2">
                <h6 className="fs-14 fw-bold mb-1">Adrian Herman</h6>
                <p className="fs-12">System Admin</p>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between menu-item mb-3">
              <div>
                <Link
                  to={route.newdashboard}
                  className="btn btn-sm btn-icon bg-light"
                >
                  <i className="ti ti-layout-grid-remove" />
                </Link>
              </div>
              <div>
                <Link to={route.chat} className="btn btn-sm btn-icon bg-light">
                  <i className="ti ti-brand-hipchat" />
                </Link>
              </div>
              <div>
                <Link
                  to={route.email}
                  className="btn btn-sm btn-icon bg-light position-relative"
                >
                  <i className="ti ti-message" />
                </Link>
              </div>
              <div className="notification-item">
                <Link
                  to={route.activities}
                  className="btn btn-sm btn-icon bg-light position-relative"
                >
                  <i className="ti ti-bell" />
                  <span className="notification-status-dot" />
                </Link>
              </div>
              <div className="me-0">
                <Link
                  to={route.generalsettings}
                  className="btn btn-sm btn-icon bg-light"
                >
                  <i className="ti ti-settings" />
                </Link>
              </div>
            </div>
          </div>
        </>

        <Scrollbars>
          <div className="sidebar-inner slimscroll mb-5">
            <div id="sidebar-menu" className="sidebar-menu">
              <ul>
                {filteredSidebarData?.map((mainLabel, index) => (
                  <li className="submenu-open" key={index}>
                    <h2 className="submenu-hdr">{mainLabel?.label}</h2>
                    <ul>
                      {mainLabel?.submenuItems?.map((title, i) => {
                        const link_array = [];
                        title?.submenuItems?.forEach((link) => {
                          link_array.push(link?.link);
                          if (link?.submenu) {
                            link?.submenuItems?.forEach((item) => {
                              link_array.push(item?.link);
                            });
                          }
                        });
                        title.links = link_array;

                        return (
                          <React.Fragment key={i}>
                            <li
                              className={`submenu ${
                                !title?.submenu &&
                                location.pathname === title?.link
                                  ? "custom-active-hassubroute-false"
                                  : ""
                              }`}
                            >
                              <Link
                                to={title?.link}
                                onClick={() => toggleSidebar(title?.label)}
                                className={`${
                                  subOpen === title?.label ? "subdrop" : ""
                                } ${
                                  title?.links?.includes(location.pathname)
                                    ? "active"
                                    : ""
                                }`}
                              >
                                <i className={`ti ti-${title.icon} me-2`}></i>
                                <span
                                  className="custom-active-span"
                                  style={{ fontSize: "18px" }}
                                >
                                  {title.label}
                                </span>
                                {title?.submenu && (
                                  <span className="menu-arrow" />
                                )}
                              </Link>

                              <ul
                                style={{
                                  display:
                                    subOpen === title?.label ? "block" : "none",
                                }}
                              >
                                {title?.submenuItems?.map((item, titleIndex) => (
                                  <li
                                    className="submenu submenu-two"
                                    key={titleIndex}
                                  >
                                    <Link
                                      to={item?.link}
                                      className={`${
                                        item?.submenuItems
                                          ?.map((l) => l.link)
                                          .includes(location.pathname) ||
                                        item?.link === location.pathname
                                          ? "active"
                                          : ""
                                      } ${
                                        subsidebar === item?.label
                                          ? "subdrop"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        toggleSubsidebar(item?.label)
                                      }
                                    >
                                      {item?.label}
                                      {item?.submenu && (
                                        <span className="menu-arrow inside-submenu" />
                                      )}
                                    </Link>

                                    <ul
                                      style={{
                                        display:
                                          subsidebar === item?.label
                                            ? "block"
                                            : "none",
                                      }}
                                    >
                                      {item?.submenuItems?.map(
                                        (items, subIndex) => (
                                          <li key={subIndex}>
                                            <Link
                                              to={items?.link}
                                              className={`${
                                                subsidebar === items?.label
                                                  ? "submenu-two subdrop"
                                                  : "submenu-two"
                                              } ${
                                                items?.submenuItems
                                                  ?.map((l) => l.link)
                                                  .includes(location.pathname) ||
                                                items?.link === location.pathname
                                                  ? "active"
                                                  : ""
                                              }`}
                                            >
                                              {items?.label}
                                            </Link>
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </li>
                                ))}
                              </ul>
                            </li>
                          </React.Fragment>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Scrollbars>
      </div>

      <HorizontalSidebar />
    </div>
  );
};

export default Sidebar;
